import React, { useEffect, useRef, useState } from "react";
import {
  blockBrowserAutofillProps,
  isUserInputEvent,
  revertUntrustedInput,
} from "../../lib/blockBrowserAutofill";
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "../ui/input";
import { CyclingPlaceholderField } from "../ui/cycling-placeholder-field";
import { cn } from "../../lib/utils";
import {
  isInboxSearchPath,
  useMessagingSearch,
} from "../../context/MessagingSearchContext";
import {
  EXPLORE_SEARCH_PLACEHOLDERS,
  INBOX_SEARCH_PLACEHOLDERS,
} from "../../lib/searchPlaceholders";
import { searchLocation } from "../../api/api";
import {
  buildExploreSearchParams,
  cityGhostParts,
  formatNominatimCity,
  parseExplorePlace,
  pickCloseCityMatch,
} from "../../lib/explorePlace";

/** Clear the query after this much idle time with no typing. */
const IDLE_CLEAR_MS = 60_000;
const CITY_SUGGEST_DEBOUNCE_MS = 300;
const CITY_QUERY_MIN_CHARS = 2;

function SearchIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-text-muted"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </svg>
  );
}

function PlacePinIcon() {
  return (
    <svg
      className="h-3 w-3 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 21s7-5.4 7-11a7 7 0 10-14 0c0 5.6 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.25" />
    </svg>
  );
}

/**
 * Committed city chip inside the explore search field.
 * @param {{ label: string, onRemove: () => void }} props
 * @returns {JSX.Element}
 */
function CityPlacePill({ label, onRemove }) {
  return (
    <span className="inline-flex max-w-[11rem] shrink-0 items-center gap-1 rounded-full border border-accent/35 bg-tag-accent py-0.5 pl-2 pr-1 text-tag-accent-fg sm:max-w-[16rem]">
      <PlacePinIcon />
      <span className="truncate text-xs font-semibold leading-none">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-tag-accent-fg hover:bg-accent/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-label={`Remove ${label}`}
      >
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden>
          <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </span>
  );
}

/**
 * Global header search — explore by default; chats/messages on messaging pages.
 *
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function GlobalSearch({ className }) {
  const navigate = useNavigate();
  const location = useLocation();
  const messagingSearch = useMessagingSearch();
  const [search, setSearch] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const inputRef = useRef(null);
  const suggestSeqRef = useRef(0);
  const [searchReady, setSearchReady] = useState(false);

  const isInboxSearch = isInboxSearchPath(location.pathname);
  const isEmpty = search.trim() === "";
  const placeholders = isInboxSearch
    ? INBOX_SEARCH_PLACEHOLDERS
    : EXPLORE_SEARCH_PLACEHOLDERS;
  const ghost = !isInboxSearch && !selectedPlace && suggestion
    ? cityGhostParts(search, suggestion)
    : null;

  useEffect(() => {
    if (isInboxSearch || location.pathname !== "/explore") return;
    const params = new URLSearchParams(location.search);
    setSearch(params.get("q") || "");
    setSelectedPlace(parseExplorePlace(params));
  }, [isInboxSearch, location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname === "/explore" && !isInboxSearch) return;
    setSelectedPlace(null);
    setSuggestion(null);
  }, [isInboxSearch, location.pathname]);

  useEffect(() => {
    if (!isInboxSearch) return undefined;
    if (search.trim()) {
      messagingSearch.setQuery(search);
    }
    return undefined;
    // Push leftover header text into inbox search when entering messaging/org channels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInboxSearch]);

  useEffect(() => {
    if (isEmpty && !selectedPlace) return undefined;
    const id = window.setTimeout(() => {
      setSearch("");
      setSelectedPlace(null);
      setSuggestion(null);
      if (isInboxSearch) {
        messagingSearch.setQuery("");
        return;
      }
      if (location.pathname === "/explore") {
        const searchStr = buildExploreSearchParams(location.search, {
          query: "",
          place: null,
        });
        navigate(
          { pathname: "/explore", search: searchStr ? `?${searchStr}` : "" },
          { replace: true }
        );
      }
    }, IDLE_CLEAR_MS);
    return () => window.clearTimeout(id);
  }, [
    search,
    selectedPlace,
    isEmpty,
    isInboxSearch,
    location.pathname,
    location.search,
    navigate,
    messagingSearch,
  ]);

  useEffect(() => {
    if (isInboxSearch || selectedPlace) {
      setSuggestion(null);
      return undefined;
    }
    const typed = search.trim();
    if (typed.length < CITY_QUERY_MIN_CHARS) {
      setSuggestion(null);
      return undefined;
    }
    const seq = ++suggestSeqRef.current;
    const timeoutId = window.setTimeout(async () => {
      const res = await searchLocation(typed, "city");
      if (seq !== suggestSeqRef.current) return;
      const rows = Array.isArray(res?.data) ? res.data : [];
      const cities = [];
      const seen = new Set();
      for (const row of rows) {
        const city = formatNominatimCity(row);
        if (!city) continue;
        const key = city.display.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        cities.push(city);
      }
      setSuggestion(pickCloseCityMatch(typed, cities));
    }, CITY_SUGGEST_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isInboxSearch, search, selectedPlace]);

  /**
   * Push the current query and optional city onto `/explore`.
   * @param {string} query
   * @param {import("../../lib/explorePlace").ExplorePlace|null} [place]
   * @param {{ replace?: boolean }} [options]
   */
  function navigateExplore(query, place = selectedPlace, options = {}) {
    const currentSearch = location.pathname === "/explore" ? location.search : "";
    const searchStr = buildExploreSearchParams(currentSearch, { query, place });
    navigate(
      { pathname: "/explore", search: searchStr ? `?${searchStr}` : "" },
      { replace: Boolean(options.replace) }
    );
  }

  function handleSearch(value) {
    setSearch(value);
    if (isInboxSearch) {
      messagingSearch.setQuery(value);
      return;
    }
    navigateExplore(value, selectedPlace);
  }

  /**
   * Accept only keystrokes in this field. Autofill into the header search
   * must not change the query or open Explore.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  function handleSearchChange(event) {
    if (!isUserInputEvent(event)) {
      revertUntrustedInput(event, search);
      return;
    }
    handleSearch(event.target.value);
  }

  /**
   * @param {React.FocusEvent<HTMLInputElement>} event
   * @returns {void}
   */
  function enableSearchEditing(event) {
    event.currentTarget.readOnly = false;
    setSearchReady(true);
  }

  function commitPlace(city) {
    if (!city) return;
    setSelectedPlace(city);
    setSearch("");
    setSuggestion(null);
    navigateExplore("", city);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  /**
   * Clicking the search bar accepts the in-bar city suggestion (same as Tab/Enter).
   * @param {React.MouseEvent} event
   */
  function handleBarMouseDown(event) {
    if (isInboxSearch || selectedPlace || !suggestion) return;
    event.preventDefault();
    commitPlace(suggestion);
  }

  function clearPlace() {
    setSelectedPlace(null);
    setSuggestion(null);
    navigateExplore(search, null);
  }

  /**
   * @param {React.KeyboardEvent<HTMLInputElement>} event
   */
  function handleKeyDown(event) {
    if (isInboxSearch) return;
    if ((event.key === "Tab" || event.key === "Enter") && suggestion && !selectedPlace) {
      event.preventDefault();
      commitPlace(suggestion);
      return;
    }
    if (event.key === "Backspace" && search === "" && selectedPlace) {
      event.preventDefault();
      clearPlace();
      return;
    }
    if (event.key === "Escape" && suggestion) {
      event.preventDefault();
      setSuggestion(null);
    }
  }

  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 items-center gap-1.5 rounded-full border border-transparent bg-primary px-2 py-1 max-w-xl transition-colors sm:gap-2 sm:px-3 sm:py-1.5",
        "focus-within:border-text-muted/40 focus-within:bg-highlight",
        className
      )}
      onMouseDown={handleBarMouseDown}
    >
      <SearchIcon />
      {!isInboxSearch && selectedPlace ? (
        <CityPlacePill label={selectedPlace.display} onRemove={clearPlace} />
      ) : null}
      <div className="relative min-w-0 flex-1">
        {ghost?.suffix ? (
          <div
            className="pointer-events-none absolute inset-0 flex items-center overflow-hidden text-base md:text-sm"
            aria-hidden
          >
            <span className="invisible whitespace-pre">{ghost.prefix}</span>
            <span className="min-w-0 truncate text-text-muted">{ghost.suffix}</span>
          </div>
        ) : null}
        <CyclingPlaceholderField
          placeholders={placeholders}
          isEmpty={!selectedPlace && isEmpty && !ghost?.suffix}
          overlayClassName="text-base md:text-sm"
        >
          <Input
            ref={inputRef}
            name="assembly-nav-query"
            value={search}
            readOnly={!searchReady}
            onFocus={enableSearchEditing}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            placeholder=""
            className="h-7 min-w-0 border-0 bg-transparent px-0 shadow-none placeholder:text-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            aria-label={isInboxSearch ? "Search chats and messages" : "Search"}
            aria-autocomplete={isInboxSearch ? undefined : "inline"}
            data-global-search=""
            {...blockBrowserAutofillProps}
          />
        </CyclingPlaceholderField>
      </div>
    </div>
  );
}
